import React, { useRef } from "react";
import { toast } from "sonner";

const MAX_BYTES = 1_500_000; // ~1.5 MB — enough for a decent biodata photo, keeps DOCX/PDF sane
const ACCEPTED = "image/jpeg,image/png,image/webp";

/**
 * Photo picker that reads the file locally, downscales to <=600px on the long
 * edge, and returns a base64 data URL via onChange. Also accepts a URL as
 * fallback for hosted images.
 */
export default function PhotoUploader({ value, onChange }) {
  const inputRef = useRef();

  const onFile = async (file) => {
    if (!file) return;
    if (!file.type.startsWith("image/")) {
      toast.error("Please pick an image file.");
      return;
    }
    if (file.size > MAX_BYTES * 2) {
      toast.error("Image is too large. Please pick something under 3 MB.");
      return;
    }
    try {
      const dataUrl = await downscaleToDataUrl(file, 600);
      onChange(dataUrl);
      toast.success("Photo added");
    } catch (e) {
      toast.error("Could not read the image. Try another file.");
    }
  };

  const onDrop = (e) => {
    e.preventDefault();
    const f = e.dataTransfer.files?.[0];
    if (f) onFile(f);
  };

  const clear = (e) => {
    e.stopPropagation();
    onChange("");
    if (inputRef.current) inputRef.current.value = "";
  };

  const hasValue = Boolean(value);

  return (
    <div>
      <div
        className={"sb-photo-drop" + (hasValue ? " has-file" : "")}
        onClick={() => inputRef.current?.click()}
        onDragOver={(e) => e.preventDefault()}
        onDrop={onDrop}
        role="button"
        tabIndex={0}
        onKeyDown={(e) => (e.key === "Enter" || e.key === " ") && inputRef.current?.click()}
        data-testid="photo-dropzone"
      >
        {hasValue ? (
          <img src={value} alt="Selected" className="sb-photo-thumb" />
        ) : (
          <div className="sb-photo-thumb empty">No photo</div>
        )}
        <div className="sb-photo-actions">
          <div className="fw-semibold" style={{ color: "var(--sb-heading)" }}>
            {hasValue ? "Change photo" : "Upload photo"}
          </div>
          <div className="sb-photo-hint">Click to browse or drop a JPG / PNG / WebP here.</div>
          {hasValue && (
            <button
              type="button"
              onClick={clear}
              className="btn btn-sm btn-link p-0 text-danger"
              style={{ alignSelf: "flex-start" }}
              data-testid="photo-clear"
            >
              Remove
            </button>
          )}
        </div>
        <input
          ref={inputRef}
          type="file"
          accept={ACCEPTED}
          className="d-none"
          onChange={(e) => onFile(e.target.files?.[0])}
          data-testid="photo-input"
        />
      </div>

      <div className="mt-2">
        <label className="form-label mb-1">…or paste a photo URL</label>
        <input
          type="url"
          className="form-control form-control-sm"
          placeholder="https://…"
          value={value?.startsWith("data:") ? "" : value || ""}
          onChange={(e) => onChange(e.target.value)}
          data-testid="photo-url-input"
        />
      </div>
    </div>
  );
}

/** Downscale using an offscreen canvas so the resulting base64 stays small. */
function downscaleToDataUrl(file, maxEdge) {
  return new Promise((resolve, reject) => {
    const reader = new FileReader();
    reader.onerror = () => reject(reader.error);
    reader.onload = () => {
      const img = new Image();
      img.onerror = () => reject(new Error("Could not decode image"));
      img.onload = () => {
        const scale = Math.min(1, maxEdge / Math.max(img.width, img.height));
        const w = Math.round(img.width * scale);
        const h = Math.round(img.height * scale);
        const canvas = document.createElement("canvas");
        canvas.width = w;
        canvas.height = h;
        const ctx = canvas.getContext("2d");
        ctx.drawImage(img, 0, 0, w, h);
        // Prefer JPEG for smaller payload; keep PNG when file was PNG w/ transparency
        const mime = file.type === "image/png" ? "image/png" : "image/jpeg";
        const quality = mime === "image/jpeg" ? 0.86 : undefined;
        resolve(canvas.toDataURL(mime, quality));
      };
      img.src = reader.result;
    };
    reader.readAsDataURL(file);
  });
}
